
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Home, Upload, User, TrendingUp, Bell, Mail, Unlock, X, Send, MessageSquare, Search, Zap, Music, Loader2, Disc, CreditCard, DollarSign, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import catSkullLogo from "@/assets/soundope-cat-skull-logo.png";
import mailboxIcon from "@/assets/icon-mail-skull.png";
import devUnlockIcon from "@/assets/dev-unlock-icon.png";
import notificationBell from "@/assets/icon-gravestone.png";
import musicNoteIcon from "@/assets/music-note-fresh.png";
import dollarBillsIcon from "@/assets/dollar-bills-fresh.png";

import navSkullOrange from "@/assets/nav-skull-orange.png";
import navSkullGreen from "@/assets/nav-skull-green.png";
import navSkullRed from "@/assets/nav-skull-red.png";
import navSkullYellow from "@/assets/nav-skull-yellow.png";
import navSkullPink from "@/assets/nav-skull-pink.png";
import navSkullCyan from "@/assets/nav-skull-cyan.png";
import navSkullBlue from "@/assets/nav-skull-blue.png";
import navSkullBlueHeadphones from "@/assets/nav-skull-blue-headphones.png";
import navSkullZebra from "@/assets/nav-skull-zebra.png";
import curateIcon from "@/assets/curate-icon.png";
import purpleMoneyIcon from "@/assets/purple-money-single.png";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Footer from "@/components/Footer";
import ContestBanner from "@/components/ContestBanner";

import { PlayerProvider } from "@/contexts/PlayerContext";
import GlobalPlayer from "@/components/GlobalPlayer";
import { WinnerBadge } from "@/components/WinnerBadge";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { hapticLight, hapticMedium, hapticSuccess, hapticError } from "@/utils/haptic";
import { useClickRateLimit } from "@/hooks/useClickRateLimit";

import AdminPanel from "@/components/AdminPanel";
import BlogAdmin from "@/components/BlogAdmin";
import NotificationsModal from "@/components/NotificationsModal";
import MessagesModal from "@/components/MessagesModal";
import MobileSidebar from "@/components/MobileSidebar";
import MobileHeaderDropdown from "@/components/MobileHeaderDropdown";
import SupportChatBot from "@/components/SupportChatBot";
import PropTypes from "prop-types";

// Helper component to show leaderboard badge for a user
function UserLeaderboardBadge({ userId }) {
  const { data: position } = useLeaderboardPosition(userId);
  return position ? <LeaderboardBadge position={position} /> : null;
}
UserLeaderboardBadge.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

// Helper component to show leaderboard badge for an artist
function ArtistLeaderboardBadge({ artistId }) {
  const { data: position } = useLeaderboardPosition(artistId);
  return position ? <LeaderboardBadge position={position} /> : null;
}
ArtistLeaderboardBadge.propTypes = {
  artistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [displayCredits, setDisplayCredits] = useState(0); // Separate state for credits display
  const [showDevModal, setShowDevModal] = useState(false);
  const [devPassword, setDevPassword] = useState("");
  const [devError, setDevError] = useState("");
  const [devUnlocked, setDevUnlocked] = useState(false);
  const [voteBoostMode, setVoteBoostMode] = useState(false);
  const [voteInputVisible, setVoteInputVisible] = useState(false);
  const [voteInputPosition, setVoteInputPosition] = useState({ x: 0, y: 0 });
  const [voteInputValue, setVoteInputValue] = useState("");
  const [selectedTrackForBoost, setSelectedTrackForBoost] = useState(null);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const voteInputRef = useRef(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showPlayer, setShowPlayer] = useState(() => {
    // Persist player visibility preference in localStorage
    const saved = localStorage.getItem('soundope_show_player');
    return saved !== null ? saved === 'true' : true;
  });
  const [showMobileBalance, setShowMobileBalance] = useState(false);
  const [desktopNavCollapsed, setDesktopNavCollapsed] = useState(false);
  
  // Rate limiting for bottom nav and all buttons
  const { checkRateLimit: checkNavRateLimit, isBlocked: isNavBlocked } = useClickRateLimit({
    maxClicks: 4,
    windowMs: 2000,
    cooldownMs: 1500,
    message: "Slow down there partner! 🤠"
  });
  
  // Global button rate limiter for all interactive elements
  const { checkRateLimit: checkGlobalRateLimit, isBlocked: isGlobalBlocked } = useClickRateLimit({
    maxClicks: 6,
    windowMs: 2000,
    cooldownMs: 1200,
    message: "Slow down there partner! 🤠"
  });
  
  
  // Ref to store latest load functions for event listeners
  const loadFunctionsRef = useRef({ loadCounts: null, loadNotifications: null, loadMessages: null });
  
  // Dev unlock sequence state
  // Sequence: 3 purple (Soundope), 2 white bat (Dead Ent), 1 purple (Soundope), 2 white bat (Dead Ent)
  const [soundopeClicks, setSoundopeClicks] = useState(0); // Purple clicks
  const [deadEntClicks, setDeadEntClicks] = useState(0); // White bat clicks
  const [sequenceStep, setSequenceStep] = useState(0); // 0: need 3 purple, 1: need 2 white, 2: need 1 purple, 3: need 2 white
  const [showDevUnlock, setShowDevUnlock] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const clickTimeoutRef = useRef(null);

  // Modal states
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [messageThreads, setMessageThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messageText, setMessageText] = useState("");
  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState({ users: [], tracks: [] });
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const notifDropdownRef = useRef(null);
  const messagesDropdownRef = useRef(null);
  const searchDropdownRef = useRef(null);



  // Check policy acceptance for protected routes
  const checkPolicyAcceptance = useCallback(async () => {
    const publicPages = [
      createPageUrl("Home"),
      createPageUrl("HowItWorks"),
      createPageUrl("ForArtists"),
      createPageUrl("FAQ"),
      createPageUrl("CookiePolicy"),
      createPageUrl("TOS"),
      createPageUrl("EULA"),
      createPageUrl("PrivacyPolicy"),
      createPageUrl("Disclaimer"),
      createPageUrl("AcceptableUsePolicy"),
      createPageUrl("CookiesRequired"),
      createPageUrl("Auth"),
    ];

    if (publicPages.includes(location.pathname) || isLoggingOut) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profileError || !profile) return;
      // TODO: enforce policy acceptance flags when added to profiles
    } catch {
      // ignore if not logged in
    }
  }, [location.pathname, isLoggingOut]);

  useEffect(() => {
    if (isLoggingOut) return;
    checkPolicyAcceptance();
  }, [location.pathname, isLoggingOut, checkPolicyAcceptance]);

  // Listen for login events to refresh user state
  // This must be after functions are defined, so we'll set it up in the same useEffect that updates the ref

  // Dev flags are NOT persisted in production - they reset on page reload
  // In development only, allow localStorage persistence for convenience
  // SECURITY: showDevUnlock is NEVER persisted - always requires click sequence
  useEffect(() => {
    if (import.meta.env.DEV) {
      const boostMode = localStorage.getItem('vote_boost_mode') === 'true';
      setVoteBoostMode(boostMode);
      
      // Note: We intentionally do NOT persist showDevUnlock
      // The admin must always complete the click sequence to reveal the dev button
      const devUnlockedValue = localStorage.getItem('dev_unlocked') === 'true';
      if (devUnlockedValue) {
        setDevUnlocked(true);
        // Do NOT set showDevUnlock here - require sequence every time
      }
    }
    // In production, both flags default to false (set in useState above)
    // Ensure showDevUnlock is always reset on mount
    setShowDevUnlock(false);
  }, []);

  // Track Shift key for vote boost
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Setup realtime subscription for vote notifications
  useEffect(() => {
    if (!currentUser || isLoggingOut) return;

    const channel = supabase
      .channel('vote-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'votes'
        },
        async (payload) => {
          try {
            // Get track details to check if it belongs to current user
            const { data: track, error: trackError } = await supabase
              .from('tracks')
              .select('artist_id, title')
              .eq('id', payload.new.track_id)
              .single();

            if (trackError || !track) return;

            // Only show notification if the vote is for current user's track and not from themselves
            if (track.artist_id === currentUser.id && payload.new.voter_id !== currentUser.id) {
              // Get voter details
              const { data: voter } = await supabase
                .from('public_profiles')
                .select('username')
                .eq('id', payload.new.voter_id)
                .single();

              // Show toast notification
              toast.success(`${voter?.username || 'Someone'} voted for "${track.title}"! 🎵`);

              // Reload notification counts
              loadCounts();
              loadNotifications();
            }
          } catch (error) {
            console.error('Error processing vote notification:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser, isLoggingOut]);

  // Setup realtime subscription for credit balance updates
  useEffect(() => {
    if (!currentUser || isLoggingOut) return;

    const creditChannel = supabase
      .channel('credit-balance-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${currentUser.id}`
        },
        async (payload) => {
          try {
            // Update currentUser state with new premium_credits
            if (payload.new.premium_credits !== undefined && payload.new.premium_credits !== currentUser.premium_credits) {
              const oldCredits = currentUser.premium_credits || 0;
              const newCredits = payload.new.premium_credits || 0;
              const difference = newCredits - oldCredits;

              setCurrentUser(prev => ({
                ...prev,
                premium_credits: newCredits
              }));

              // Show toast notification for credit changes (only for increases to avoid spam)
              if (difference > 0) {
                toast.success(`+${difference} credits! Balance: ${newCredits}`, {
                  duration: 2000
                });
              }
            }
          } catch (error) {
            console.error('Error processing credit update:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(creditChannel);
    };
  }, [currentUser, isLoggingOut]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
        setShowMobileSearch(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setCurrentUser(null);
        setUnreadNotifications(0);
        setUnreadMessages(0);
        return;
      }

      // Fetch user profile from backend
      // Retry up to 5 times with increasing delay for new accounts
      let profile = null;
      let profileError = null;
      const maxRetries = 5;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

        if (!error && data) {
          profile = data;
          break;
        }
        
        profileError = error;
        if (attempt < maxRetries - 1) {
          // Increasing delay: 500ms, 1000ms, 1500ms, 2000ms
          const delay = 500 * (attempt + 1);
          console.log(`[Layout] Profile not found, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      if (profileError && !profile) {
        console.error("Error loading profile after retries:", profileError);
        // Clear auth state if profile truly doesn't exist - user needs to re-register
        console.warn("[Layout] Profile not found after max retries, clearing session");
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('auth_token');
          window.localStorage.removeItem('auth_user');
        }
        setCurrentUser(null);
        setUnreadNotifications(0);
        setUnreadMessages(0);
        return;
      }
      
      if (!profile) {
        console.warn("Profile not found for user:", session.user.id);
        // Same handling - clear stale session
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('auth_token');
          window.localStorage.removeItem('auth_user');
        }
        setCurrentUser(null);
        return;
      }

      // Check if we need to reset weekly votes (Sunday 11:59 PM)
      const now = new Date();
      const lastReset = profile.last_votes_reset 
        ? new Date(profile.last_votes_reset) 
        : new Date(0); // Default to epoch if null/undefined
      
      // Find most recent Sunday at 11:59 PM
      const lastSunday = new Date(now);
      lastSunday.setDate(now.getDate() - now.getDay()); // Go to this week's Sunday
      lastSunday.setHours(23, 59, 0, 0);
      
      // If last reset was before the most recent Sunday, reset votes
      if (lastReset < lastSunday && now >= lastSunday) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            weekly_votes_remaining: 10,
            last_votes_reset: new Date().toISOString()
          })
          .eq('id', session.user.id);
        
        // Only update local state if database update succeeded
        if (!updateError) {
        profile.weekly_votes_remaining = 10;
        profile.last_votes_reset = new Date().toISOString();
        } else {
          console.error('Error resetting weekly votes:', updateError);
        }
      }

      // Set user with profile data - use actual database columns
      const totalCredits = (profile.premium_credits || 0) + (profile.earned_credits || 0);
      setDisplayCredits(totalCredits); // Update separate credits state
      setCurrentUser({
        id: profile.id,
        email: profile.email,
        username: profile.username,
        artist_name: profile.artist_name || profile.username,
        full_name: profile.username,
        bio: profile.bio,
        avatar_url: profile.avatar_url,
        profile_image_url: profile.avatar_url,
        balance: profile.balance,
        standard_credits: profile.balance,
        premium_credits: profile.premium_credits || 0,
        earned_credits: profile.earned_credits || 0,
        social_links: profile.social_links || {},
        points: profile.points || 0,
        weekly_votes_remaining: profile.weekly_votes_remaining || 10,
        last_votes_reset: profile.last_votes_reset,
        has_winner_badge: profile.has_winner_badge || false
      });

      // Check admin role from database
      try {
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id);
        
        const hasAdminAccess = Array.isArray(roles) && roles.some(r => r.role === 'admin');
        console.log('[Layout] Admin check - user:', session.user.id, 'roles:', roles, 'isAdmin:', hasAdminAccess);
        setIsAdmin(hasAdminAccess);
      } catch (roleError) {
        console.error('[Layout] Error checking admin role:', roleError);
        setIsAdmin(false);
      }

      // Load notifications and messages from Supabase
      try {
        const { data: notifs } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('is_read', false);
        setUnreadNotifications((notifs || []).length);
      } catch {
        setUnreadNotifications(0);
      }

      try {
        const { data: messages } = await supabase
          .from('messages')
          .select('*')
          .eq('recipient_id', session.user.id)
          .eq('is_read', false);
        setUnreadMessages((messages || []).length);
      } catch {
        setUnreadMessages(0);
      }
    } catch (error) {
      console.error("Error loading user:", error);
      setCurrentUser(null);
      setUnreadNotifications(0);
      setUnreadMessages(0);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // For now, load from Supabase notifications table
      const { data: notifs } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      setNotifications(notifs || []);
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Load messages from the API endpoint (handles both sent and received)
      const token = localStorage.getItem('auth_token');
      const response = await fetch('https://soundope.com/api/messages', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error("Error loading messages:", response.statusText);
        setMessageThreads([]);
        return;
      }

      const messages = await response.json();

      // Group messages by conversation partner
      const threadsMap = new Map();
      
      for (const msg of messages || []) {
        const otherUserId = msg.sender_id === session.user.id ? msg.recipient_id : msg.sender_id;
        
        if (!threadsMap.has(otherUserId)) {
          threadsMap.set(otherUserId, {
            threadId: otherUserId,
            otherUserId,
            messages: [],
            unreadCount: 0
          });
        }
        
        const thread = threadsMap.get(otherUserId);
        thread.messages.push(msg);
        
        // Count unread messages (received and not read)
        if (msg.recipient_id === session.user.id && !msg.is_read) {
          thread.unreadCount++;
        }
      }

      // Fetch usernames for all conversation partners from Supabase public_profiles
      const partnerIds = Array.from(threadsMap.keys());
      
      if (partnerIds.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, username, artist_name, avatar_url")
          .in("id", partnerIds);
        
        if (profiles) {
          for (const profile of profiles) {
            const thread = threadsMap.get(profile.id);
            if (thread) {
              thread.otherUsername = profile.username || profile.artist_name || 'Unknown';
              thread.otherAvatar = profile.avatar_url;
            }
          }
        }
      }

      // Convert to array and sort by most recent message
      const threads = Array.from(threadsMap.values()).map(thread => ({
        ...thread,
        lastMessage: thread.messages[0]?.content || "",
        lastMessageTime: thread.messages[0]?.created_at
      })).sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));

      setMessageThreads(threads);
    } catch (error) {
      console.error("Error loading messages:", error);
      setMessageThreads([]);
    }
  }, []);

  // Update ref when functions are defined and load user data on mount/path change
  useEffect(() => {
    loadFunctionsRef.current = { loadCounts, loadNotifications, loadMessages };
    
    // Load user data when path changes (but only if not logging out)
    if (!isLoggingOut) {
      loadCounts();
      loadNotifications();
      loadMessages();
    }
  }, [location.pathname, isLoggingOut, loadCounts, loadNotifications, loadMessages]);

  // Set up Supabase auth state change listener for real-time auth updates
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Layout] Auth state changed:', event, session ? 'has session' : 'no session');
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Small delay to ensure profile is created for new signups
        setTimeout(() => {
          if (loadFunctionsRef.current?.loadCounts) {
            loadFunctionsRef.current.loadCounts();
          }
          if (loadFunctionsRef.current?.loadNotifications) {
            loadFunctionsRef.current.loadNotifications();
          }
          if (loadFunctionsRef.current?.loadMessages) {
            loadFunctionsRef.current.loadMessages();
          }
        }, 300);
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        setUnreadNotifications(0);
        setUnreadMessages(0);
        setIsAdmin(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Listen for login events to refresh user state (after functions are defined)
  useEffect(() => {
    const handleLogin = () => {
      console.log('[Layout] Login event detected, refreshing user state');
      // Small delay to ensure cookies are set
      setTimeout(() => {
        if (loadFunctionsRef.current?.loadCounts) {
          loadFunctionsRef.current.loadCounts();
        }
        if (loadFunctionsRef.current?.loadNotifications) {
          loadFunctionsRef.current.loadNotifications();
        }
        if (loadFunctionsRef.current?.loadMessages) {
          loadFunctionsRef.current.loadMessages();
        }
      }, 100);
    };

    const handleCreditsUpdated = (event) => {
      console.log('[Layout] Credits updated event detected, refreshing user state', event?.detail);
      const newBalance = event?.detail?.newBalance;
      if (typeof newBalance === 'number') {
        console.log('[Layout] Updating displayCredits to:', newBalance);
        // Update BOTH the separate credits state AND currentUser
        setDisplayCredits(newBalance + (currentUser?.earned_credits || 0));
        setCurrentUser(prev => prev ? { ...prev, premium_credits: newBalance } : prev);
      } else if (loadFunctionsRef.current?.loadCounts) {
        loadFunctionsRef.current.loadCounts();
      }
    };

    const handleEarnedCreditsUpdated = (event) => {
      console.log('[Layout] Earned credits updated event detected', event?.detail);
      const newBalance = event?.detail?.newBalance;
      if (typeof newBalance === 'number') {
        setCurrentUser(prev => prev ? { ...prev, earned_credits: newBalance } : prev);
      }
    };

    const handleMessageSent = () => {
      console.log('[Layout] Message sent event detected, refreshing messages');
      if (loadFunctionsRef.current?.loadMessages) {
        loadFunctionsRef.current.loadMessages();
      }
    };

    window.addEventListener('auth:login', handleLogin);
    window.addEventListener('credits:updated', handleCreditsUpdated);
    window.addEventListener('earnedCreditsUpdated', handleEarnedCreditsUpdated);
    window.addEventListener('messageSent', handleMessageSent);
    
    return () => {
      window.removeEventListener('auth:login', handleLogin);
      window.removeEventListener('credits:updated', handleCreditsUpdated);
      window.removeEventListener('earnedCreditsUpdated', handleEarnedCreditsUpdated);
      window.removeEventListener('messageSent', handleMessageSent);
    };
  }, [loadCounts, loadNotifications, loadMessages]);

  // Perform search function
  const performSearch = useCallback(async (query) => {
    if (!query || !query.trim()) {
      setSearchResults({ users: [], tracks: [] });
      setShowSearchDropdown(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setShowSearchDropdown(true);

    try {
      const normalizedQuery = query.toLowerCase().trim();

      // Search users by username
      const { data: users } = await supabase
        .from('public_profiles')
        .select('id, username, avatar_url, bio, has_winner_badge')
        .ilike('username', `%${normalizedQuery}%`)
        .limit(5);

      // Search tracks by title or artist name
      const { data: tracks } = await supabase
        .from('tracks')
        .select('id, title, artist_name, artist_id, cover_image_url')
        .or(`title.ilike.%${normalizedQuery}%,artist_name.ilike.%${normalizedQuery}%`)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(5);

      const matchedUsers = (users || [])
        .filter(user => user.id !== currentUser?.id)
        .map(user => ({
          id: user.id,
          artist_name: user.username,
          full_name: user.username,
          profile_image_url: user.avatar_url,
          has_winner_badge: user.has_winner_badge || false
        }));

      // Fetch badge status for track artists
      let artistBadgeMap = new Map();
      if (tracks && tracks.length > 0) {
        const artistIds = [...new Set(tracks.map(t => t.artist_id))];
        const { data: artistProfiles } = await supabase
          .from('profiles')
          .select('id, has_winner_badge')
          .in('id', artistIds);
        artistBadgeMap = new Map(artistProfiles?.map(p => [p.id, p.has_winner_badge]) || []);
      }

      const matchedTracks = (tracks || []).map(track => ({
        id: track.id,
        title: track.title,
        artist_name: track.artist_name,
        artist_id: track.artist_id,
        cover_image_url: track.cover_image_url,
        artist_has_badge: artistBadgeMap.get(track.artist_id) || false
      }));

      setSearchResults({ users: matchedUsers, tracks: matchedTracks });
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults({ users: [], tracks: [] });
      setShowSearchDropdown(false);
    } finally {
      setIsSearching(false);
    }
  }, [currentUser]);

  // Debounced search on typing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim()) {
        performSearch(searchQuery);
      } else {
        setSearchResults({ users: [], tracks: [] });
        setShowSearchDropdown(false);
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, performSearch]);

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.is_read) {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('id', notif.id);
        loadCounts();
        loadNotifications();
      }
      
      setShowNotificationsModal(false);
      
      // Navigate based on reference_type and reference_id if available
      if (notif.reference_id && notif.reference_type) {
        switch (notif.reference_type) {
          case 'track':
            // Go directly to track details
            navigate(createPageUrl('TrackDetails') + `?id=${notif.reference_id}`);
            return;
          case 'feedback':
          case 'vote':
            // For feedback/vote, check if there's feedback to show
            try {
              const { data: feedbackList } = await supabase
                .from('feedback')
                .select('id')
                .eq('track_id', notif.reference_id)
                .order('created_at', { ascending: false })
                .limit(1);
              
              if (feedbackList && feedbackList.length > 0) {
                navigate(createPageUrl('ViewFeedback') + `?id=${feedbackList[0].id}`);
                return;
              }
            } catch (feedbackError) {
              console.warn('Could not find feedback:', feedbackError);
            }
            // Fallback to track details if no feedback found
            navigate(createPageUrl('TrackDetails') + `?id=${notif.reference_id}`);
            return;
          case 'profile':
            navigate(createPageUrl('PublicProfile') + `?id=${notif.reference_id}`);
            return;
          case 'message':
            setShowMessagesModal(true);
            return;
          default:
            navigate(createPageUrl('TrackDetails') + `?id=${notif.reference_id}`);
            return;
        }
      }
      
      // Fallback: extract track title from message and look it up
      const titleMatch = notif.message?.match(/"([^"]+)"/);
      if (titleMatch) {
        const trackTitle = titleMatch[1];
        try {
          // Look up track by title
          const { data: tracks } = await supabase
            .from('tracks')
            .select('id')
            .ilike('title', trackTitle)
            .limit(1);
          
          if (tracks && tracks.length > 0) {
            navigate(createPageUrl('TrackDetails') + `?id=${tracks[0].id}`);
            return;
          }
        } catch (lookupError) {
          console.warn('Could not find track for notification:', lookupError);
        }
      }
      
      // Determine fallback based on notification title/type
      const title = notif.title?.toLowerCase() || '';
      if (title.includes('vote') || title.includes('feedback')) {
        navigate(createPageUrl('ReceivedFeedback'));
      } else if (title.includes('track') || title.includes('upload') || title.includes('pitch')) {
        navigate(createPageUrl('MyTracks'));
      } else if (title.includes('message')) {
        setShowMessagesModal(true);
      } else {
        // Default to notifications page
        navigate(createPageUrl('Notifications'));
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    if (!currentUser) return;

    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', currentUser.id)
        .eq('is_read', false);
      
      loadCounts();
      loadNotifications();
    } catch (error) {
      console.error('Error marking all notifications read:', error);
    }
  };

  const handleThreadClick = async (thread) => {
    setSelectedThread(thread);
    if (!currentUser) return;
    
    // Mark thread messages as read
    const messageIds = thread.messages
      .filter(m => !m.is_read && m.recipient_id === currentUser.id)
      .map(m => m.id);
    
    if (messageIds.length > 0) {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .in('id', messageIds);
    }
    
    loadCounts();
    loadMessages();
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedThread || !currentUser) return;

    const thread = selectedThread.messages[0];
    const recipientId = thread.sender_id === currentUser.id ? thread.recipient_id : thread.sender_id;

    await supabase
      .from('messages')
      .insert({
        sender_id: currentUser.id,
        recipient_id: recipientId,
        content: messageText.trim(),
        thread_id: selectedThread.threadId
      });

    setMessageText("");
    loadMessages();
    
    // Reload thread messages
    const { data: updated } = await supabase
      .from('messages')
      .select('*')
      .eq('thread_id', selectedThread.threadId)
      .order('created_at', { ascending: false });
    
    if (updated) {
      setSelectedThread({
        ...selectedThread,
        messages: updated
      });
    }
  };

  const handleSearchResultClick = (type, id) => {
    setSearchQuery("");
    setShowSearchDropdown(false);
    setShowMobileSearch(false);
    setSearchResults({ users: [], tracks: [] });
    
    if (type === 'user') {
      navigate(createPageUrl("PublicProfile") + `?userId=${id}`);
    } else if (type === 'track') {
      navigate(createPageUrl("TrackDetails") + `?trackId=${id}`);
    }
  };

  const handleSearchIconClick = () => {
    if (searchQuery.trim()) {
      performSearch(searchQuery);
      setShowSearchDropdown(true);
    }
  };

  const handleMobileSearchToggle = () => {
    setShowMobileSearch(!showMobileSearch);
    if (!showMobileSearch) {
      // Focus on input when opening
      setTimeout(() => {
        document.getElementById('mobile-search-input')?.focus();
      }, 100);
    } else {
      // Clear search when closing
      setSearchQuery("");
      setShowSearchDropdown(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      performSearch(searchQuery);
      setShowSearchDropdown(true);
    }
  };

  const handleDevUnlock = async () => {
    setDevError("");

    try {
      // Ensure user is logged in
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        setDevError("Not logged in");
        return;
      }

      // Check if user has admin role via Supabase
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin");

      if (rolesError) {
        console.error("[Dev Unlock] Error checking roles:", rolesError);
        setDevError("Failed to verify permissions");
        return;
      }

      if (!roles || roles.length === 0) {
        setDevError("Access denied - admin role required");
        return;
      }

      // Admin verified - grant dev panel access
      setDevUnlocked(true);
      toast.success("Dev panel access granted");

      // Only persist in development
      if (import.meta.env.DEV) {
        localStorage.setItem("dev_unlocked", "true");
      }

      // Refresh user data
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profile) setCurrentUser(profile);
    } catch (error) {
      console.error("[Dev Unlock] Error:", error);
      setDevError(
        "Failed to unlock: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  const handleRestoreLeaderboard = async () => {
    try {
      // SECURITY: This operation should be validated server-side
      // TODO: Move to server endpoint with authentication and admin role checks
      // Only persist flag in development
      if (import.meta.env.DEV) {
        localStorage.setItem('leaderboard_restored', 'true');
      }
      
      // Get all active tracks (not just current month)
      const { data: tracks, error } = await supabase
        .from("tracks")
        .select("id")
        .eq("is_active", true);

      if (error) throw error;

      toast.success(`Restored ${tracks?.length || 0} tracks to leaderboard. They will remain until auto-reset is re-enabled.`);
      setShowDevModal(false);
      setDevUnlocked(false);
      
      // Refresh leaderboard page if user is on it
      if (window.location.pathname.includes('Leaderboard')) {
        window.location.reload();
      }
    } catch (error) {
      toast.error("Failed to restore leaderboard: " + error.message);
    }
  };

  const handleEnableVoteBoost = () => {
    if (!devUnlocked) {
      toast.error("Dev menu must be unlocked first");
      return;
    }
    setVoteBoostMode(true);
    // Only persist in development - production requires server-side validation
    if (import.meta.env.DEV) {
      localStorage.setItem('vote_boost_mode', 'true');
      localStorage.setItem('dev_unlocked', 'true');
    }
    toast.info("Vote boost mode enabled. Click on any track image to add votes.");
  };

  const handleTrackClickForBoost = async (e, trackId) => {
    // Vote boost mode is controlled by state, not localStorage in production
    if (!voteBoostMode) return;
    
    // Require Shift to be held (Shift + click)
    if (!isShiftPressed) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedTrackForBoost(trackId);
    setVoteInputPosition({ x: e.clientX, y: e.clientY });
    setVoteInputVisible(true);
    setVoteInputValue("");
    
    // Focus input after a brief delay
    setTimeout(() => {
      voteInputRef.current?.focus();
    }, 100);
  };

  const handleSubmitVoteBoost = async () => {
    if (!selectedTrackForBoost || !voteInputValue) return;
    
    const votes = parseInt(voteInputValue);
    if (isNaN(votes) || votes <= 0) {
      hapticError();
      toast.error("Please enter a valid number");
      return;
    }

    hapticMedium();

    try {
      // SECURITY: This operation should be validated server-side
      // TODO: Move to server endpoint that validates:
      // - User has dev/admin privileges (session-based check)
      // - IP address whitelist (optional)
      // - Rate limiting
      // Current implementation allows client-side database modification - vulnerable in production
      
      // Get current track praise_count
      const { data: track, error: trackError } = await supabase
        .from("tracks")
        .select("praise_count")
        .eq("id", selectedTrackForBoost)
        .single();

      if (trackError) throw trackError;

      // Update track praise_count
      const { error: updateError } = await supabase
        .from("tracks")
        .update({ praise_count: (track.praise_count || 0) + votes })
        .eq("id", selectedTrackForBoost);

      if (updateError) throw updateError;

      hapticSuccess();
      toast.success(`Added ${votes} votes to track`);
      setVoteInputVisible(false);
      setVoteInputValue("");
      setSelectedTrackForBoost(null);
    } catch (error) {
      hapticError();
      toast.error("Failed to add votes: " + error.message);
    }
  };

  const resetClickSequence = () => {
    setSoundopeClicks(0);
    setDeadEntClicks(0);
    setSequenceStep(0);
    setShowDevUnlock(false);
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  };

  const startClickTimeout = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = setTimeout(() => {
      resetClickSequence();
    }, 5000); // Reset after 5 seconds of inactivity
  };

  const handleDeadEntLogoClick = () => {
    // If dev unlock is already shown, don't do anything
    if (showDevUnlock) {
      return;
    }
    
    startClickTimeout();
    
    // Sequence: 3 purple, 2 white, 1 purple, 2 white
    if (sequenceStep === 1) {
      // Need 2 white bat clicks (Dead Ent)
      if (deadEntClicks < 2) {
        const newCount = deadEntClicks + 1;
        setDeadEntClicks(newCount);
        if (newCount === 2) {
          // Completed 2 white bat clicks, move to next step (need 1 purple)
          setSequenceStep(2);
          setDeadEntClicks(0);
        }
      }
    } else if (sequenceStep === 3) {
      // Need 2 white bat clicks (Dead Ent) - final step
      if (deadEntClicks < 2) {
        const newCount = deadEntClicks + 1;
        setDeadEntClicks(newCount);
        if (newCount === 2) {
          // Completed final 2 white bat clicks, show dev unlock!
          setShowDevUnlock(true);
          if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
          }
        }
      }
    } else {
      // Wrong step - reset sequence
      resetClickSequence();
    }
  };

  const handleSoundopeLogoClick = () => {
    // If dev unlock is already shown, don't do anything
    if (showDevUnlock) {
      return;
    }
    
    startClickTimeout();
    
    // Sequence: 3 purple, 2 white, 1 purple, 2 white
    if (sequenceStep === 0) {
      // Need 3 purple clicks (Soundope)
      if (soundopeClicks < 3) {
        const newCount = soundopeClicks + 1;
        setSoundopeClicks(newCount);
        if (newCount === 3) {
          // Completed 3 purple clicks, move to next step (need 2 white)
          setSequenceStep(1);
          setSoundopeClicks(0);
        }
      }
    } else if (sequenceStep === 2) {
      // Need 1 purple click (Soundope)
      if (soundopeClicks < 1) {
        const newCount = soundopeClicks + 1;
        setSoundopeClicks(newCount);
        if (newCount === 1) {
          // Completed 1 purple click, move to final step (need 2 white)
          setSequenceStep(3);
          setSoundopeClicks(0);
        }
      }
    } else {
      // Wrong step - reset sequence
      resetClickSequence();
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);


  const navItems = [
    { icon: navSkullOrange, label: "Discover", tooltip: "Discover new tracks", path: createPageUrl("Discover"), glowColor: "255,140,0" },
    { icon: navSkullGreen, label: "Submit", tooltip: "Submit to playlists", path: createPageUrl("SubmitOptions"), glowColor: "0,255,0" },
    { icon: navSkullCyan, label: "New Tracks", tooltip: "Fresh dope sounds", path: createPageUrl("FreshDopeSounds"), glowColor: "0,255,255", longLabel: true, hideOnMobile: true },
    { icon: navSkullZebra, label: "Tools", tooltip: "Artist tools", path: createPageUrl("Tools"), glowColor: "200,200,200" },
    { icon: navSkullRed, label: "Leaderboard", tooltip: "Leaderboard", path: createPageUrl("Leaderboard"), glowColor: "255,0,0", longLabel: true },
    { icon: navSkullYellow, label: "Upload", tooltip: "Upload a new track", path: createPageUrl("Upload"), glowColor: "255,255,0" },
    { icon: navSkullPink, label: "Profile", tooltip: "View your profile", path: createPageUrl("Profile"), glowColor: "255,0,255" },
  ];

  const isActive = (path) => location.pathname === path;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'feedback_received': return MessageSquare;
      case 'comment': return MessageSquare;
      default: return Bell;
    }
  };

  return (
    <PlayerProvider>
      <div className="min-h-screen text-[#e0e0e0]">
        <style>{`
          html {
            background: radial-gradient(circle at top, hsl(0 0% 18%), hsl(0 0% 10%), hsl(0 0% 4%));
            margin: 0;
            padding: 0;
            width: 100vw;
            max-width: 100vw;
            overflow-x: hidden;
          }

          body {
            background: radial-gradient(circle at top, hsl(0 0% 18%), hsl(0 0% 10%), hsl(0 0% 4%));
            margin: 0;
            padding: 0;
            width: 100vw;
            max-width: 100vw;
            overflow-x: hidden;
            position: relative;
          }

          #root {
            background: transparent;
            min-height: 100vh;
            width: 100vw;
            max-width: 100vw;
            overflow-x: hidden;
          }

          * {
            -webkit-tap-highlight-color: transparent;
            box-sizing: border-box;
          }

          .neuro-base {
            background: #141414;
            box-shadow:
              6px 6px 12px #000000,
              -6px -6px 12px #1f1f1f;
            border: none;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .neuro-base:hover {
            box-shadow:
              8px 8px 16px #000000,
              -8px -8px 16px #1f1f1f;
          }

          .neuro-pressed {
            background: #141414;
            box-shadow:
              inset 4px 4px 8px #000000,
              inset -4px -4px 8px #1f1f1f;
          }

          .neuro-flat {
            background: #141414;
            box-shadow:
              3px 3px 6px #000000,
              -3px -3px 6px #1f1f1f;
          }

          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          ::-webkit-scrollbar-track {
            background: #0a0a0a;
          }

          ::-webkit-scrollbar-thumb {
            background: #1f1f1f;
            border-radius: 4px;
            box-shadow: inset 2px 2px 4px #000000;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: #2a2a2a;
          }

          .smooth-transition {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .text-glow {
            text-shadow: 0 0 10px rgba(128, 128, 128, 0.3);
          }

          .logo-blend {
            mix-blend-mode: lighten;
            filter: brightness(1.1) contrast(1.05);
          }

          .dropdown-optimized {
            will-change: transform, opacity;
            transform: translateZ(0);
            backface-visibility: hidden;
          }

          @media (max-width: 768px) {
            html, body, #root {
              width: 100vw;
              max-width: 100vw;
              overflow-x: hidden;
            }
            
            /* Prevent buttons and inputs from overflowing */
            button, input, textarea, select {
              max-width: 100%;
              box-sizing: border-box;
            }
            
            /* Ensure containers don't overflow */
            .max-w-screen-xl, .max-w-7xl, .max-w-6xl, .max-w-4xl, .max-w-3xl, .max-w-2xl {
              padding-left: 1rem;
              padding-right: 1rem;
            }
            
            /* File inputs on mobile */
            input[type="file"] {
              font-size: 0.875rem;
            }
            
            input[type="file"]::file-selector-button {
              font-size: 0.75rem;
              padding: 0.25rem 0.5rem;
            }
          }

          @media (min-width: 380px) {
            .xs\\:block {
              display: block !important;
            }
          }

          @supports (padding-bottom: env(safe-area-inset-bottom)) {
            .safe-area-inset-bottom {
              padding-bottom: env(safe-area-inset-bottom);
            }
          }
        `}</style>

        {/* Public Header - No Auth Required */}
        {!currentUser && (
          <div 
            className="fixed top-0 left-0 right-0 neuro-base border-b border-[#1f1f1f]" 
            style={{ 
              zIndex: 9999, 
              backgroundColor: '#141414',
              paddingTop: 'env(safe-area-inset-top, 0px)',
              height: 'calc(3.5rem + env(safe-area-inset-top, 0px))'
            }}
          >
            <div className="max-w-screen-xl mx-auto h-14 px-3 md:px-6 flex items-center justify-start gap-4 overflow-visible">
              {/* Soundope Logo */}
              <div className="flex items-center flex-shrink-0 h-full min-w-0">
                <Link to={createPageUrl("Home")} className="flex items-center h-full">
                  <img
                    src={catSkullLogo}
                    alt="Soundope Logo"
                    className="h-10 md:h-[90px] w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
                    decoding="async"
                    loading="eager"
                    fetchpriority="high"
                    width="200"
                    height="218"
                  />
                </Link>
              </div>

              <div className="flex items-center flex-shrink-0 pr-1">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Login/Signup button clicked, navigating to /auth');
                    navigate('/auth');
                  }}
                  className="neuro-base active:neuro-pressed rounded-xl px-4 py-2.5 smooth-transition hover:scale-105 cursor-pointer overflow-visible"
                >
                  <span className="text-[#b0b0b0] font-rotten text-sm tracking-wide whitespace-nowrap">Login / Signup</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Authenticated Header */}
        {currentUser && (
          <div 
            className="fixed top-0 left-0 right-0 neuro-base border-b border-[#1f1f1f]" 
            style={{ 
              zIndex: 9999, 
              backgroundColor: '#141414',
              paddingTop: 'env(safe-area-inset-top, 0px)',
              height: 'calc(3.5rem + env(safe-area-inset-top, 0px))'
            }}
          >
            <div className="h-14 max-w-screen-xl mx-auto px-2 md:px-6 flex items-center justify-start gap-3">
              {/* Logo - Always visible */}
              <div className="flex items-center gap-2 flex-shrink-0 h-full">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to={createPageUrl("Home")} className="flex items-center h-full">
                        <img
                          src={catSkullLogo}
                          alt="Soundope Logo"
                          className="h-10 md:h-[120px] w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity max-h-full"
                          decoding="async"
                          loading="eager"
                          fetchpriority="high"
                          width="200"
                          height="218"
                        />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Go to Soundope home</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Mobile: Credits + Dropdown */}
              <div className="flex md:hidden items-center gap-2">
                {/* Credits Counter */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setShowMobileBalance((prev) => !prev)}
                        className="neuro-base active:neuro-pressed rounded-lg h-8 px-2 flex items-center gap-1.5 flex-shrink-0 smooth-transition"
                      >
                        <img 
                          src={purpleMoneyIcon} 
                          alt="Credits" 
                          className="w-4 h-4 object-contain" 
                        />
                        <span className={`text-xs text-[#d0d0d0] font-medium tabular-nums transition-all duration-200 ${showMobileBalance ? 'block' : 'hidden'}`}>
                          ${(displayCredits / 100).toFixed(2)}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Tap to show balance</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Mobile Dropdown Menu */}
                <MobileHeaderDropdown
                  onOpenNotifications={() => {
                    loadNotifications();
                    setShowNotificationsModal(true);
                  }}
                  onOpenMessages={() => {
                    loadMessages();
                    setShowMessagesModal(true);
                  }}
                  onLogout={async () => {
                    setIsLoggingOut(true);
                    await supabase.auth.signOut();
                    setCurrentUser(null);
                    navigate(createPageUrl('Auth'));
                  }}
                  unreadNotifications={unreadNotifications}
                  unreadMessages={unreadMessages}
                  checkRateLimit={checkGlobalRateLimit}
                />
              </div>

              {/* Desktop: Full button row */}
              <div className="hidden md:flex items-center gap-3">
                {/* What We Offer - links to dedicated page */}
                <button
                  onClick={() => {
                    if (checkGlobalRateLimit()) return;
                    navigate(createPageUrl('WhatWeOffer'));
                  }}
                  className="neuro-base active:neuro-pressed rounded-xl h-11 px-4 flex items-center justify-center flex-shrink-0 smooth-transition hover:scale-105"
                >
                  <span className="font-masking-renta text-base text-[#b0b0b0] tracking-wide whitespace-nowrap">WHAT WE OFFER</span>
                </button>

                {/* Credit Shop - different behavior for native vs web */}
                {window.Capacitor ? (
                  <button
                    onClick={() => {
                      if (checkGlobalRateLimit()) return;
                      toast.info(
                        "Credits are how you upload, submit to playlists, and more. But we don't sell credits on mobile. Please visit our web store to get credits.\n\nsoundope.com/shop",
                        { duration: 6000 }
                      );
                    }}
                    className="neuro-base active:neuro-pressed rounded-xl h-11 px-4 flex items-center justify-center flex-shrink-0 smooth-transition hover:scale-105"
                  >
                    <span className="font-masking-renta text-base text-primary tracking-wide whitespace-nowrap">GET CREDITS</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (checkGlobalRateLimit()) return;
                      navigate(createPageUrl('Shop'));
                    }}
                    className="neuro-base active:neuro-pressed rounded-xl h-11 px-4 flex items-center justify-center flex-shrink-0 smooth-transition hover:scale-105"
                  >
                    <span className="font-masking-renta text-base text-primary tracking-wide whitespace-nowrap">GET CREDITS</span>
                  </button>
                )}

                {/* Credits Counter */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="neuro-base active:neuro-pressed rounded-xl h-11 px-3 flex items-center gap-2 flex-shrink-0 smooth-transition hover:scale-105"
                      >
                        <img 
                          src={purpleMoneyIcon} 
                          alt="Credits" 
                          className="w-5 h-5 object-contain" 
                        />
                        <span className="text-sm text-[#d0d0d0] font-medium tabular-nums">
                          ${(displayCredits / 100).toFixed(2)}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Review credits: Earn $0.01 per review</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Global Player Toggle */}
                <button
                  onClick={() => {
                    setShowPlayer((prev) => {
                      const newValue = !prev;
                      localStorage.setItem('soundope_show_player', String(newValue));
                      return newValue;
                    });
                  }}
                  className={`neuro-base active:neuro-pressed rounded-xl w-11 h-11 smooth-transition hover:scale-105 flex-shrink-0 overflow-hidden flex items-center justify-center p-1.5 ${!showPlayer ? 'opacity-50' : ''}`}
                  aria-label={showPlayer ? "Hide player" : "Show player"}
                  title={showPlayer ? "Hide global player" : "Show global player"}
                >
                  <img src={musicNoteIcon} alt="Music Player" className="w-full h-full object-contain" />
                  {!showPlayer && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-0.5 bg-red-500 rotate-45 rounded-full" />
                    </div>
                  )}
                </button>

                {/* Cashout */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          if (checkGlobalRateLimit()) return;
                          navigate('/cashout');
                        }}
                        className="neuro-base active:neuro-pressed rounded-xl w-11 h-11 smooth-transition hover:scale-105 flex-shrink-0 flex items-center justify-center"
                      >
                        <DollarSign className="w-5 h-5 text-[#b0b0b0]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Cash out earnings</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Dev Unlock - Hidden until sequence completed AND user is admin */}
                {showDevUnlock && isAdmin && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setShowDevModal(true)}
                          className="neuro-base active:neuro-pressed rounded-xl w-11 h-11 flex items-center justify-center smooth-transition hover:scale-105 flex-shrink-0 overflow-hidden p-1.5"
                        >
                          <img src={devUnlockIcon} alt="Dev Unlock" className="w-full h-full object-contain" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Dev Unlock</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Notifications Button */}
                <button
                  onClick={() => {
                    if (checkGlobalRateLimit()) return;
                    loadNotifications();
                    setShowNotificationsModal(true);
                  }}
                  className="neuro-base active:neuro-pressed rounded-xl w-11 h-11 flex items-center justify-center relative smooth-transition hover:scale-105 overflow-hidden p-1.5 flex-shrink-0"
                  title="View your notifications"
                >
                  <img src={notificationBell} alt="Notifications" className="w-full h-full object-contain" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#505050] text-[#d0d0d0] text-xs flex items-center justify-center">
                      {unreadNotifications}
                    </span>
                  )}
                </button>

                {/* Messages Button */}
                <button
                  onClick={() => {
                    if (checkGlobalRateLimit()) return;
                    loadMessages();
                    setShowMessagesModal(true);
                  }}
                  className="neuro-base active:neuro-pressed rounded-xl w-11 h-11 flex items-center justify-center relative smooth-transition hover:scale-105 overflow-hidden p-1.5 flex-shrink-0"
                  title="View your messages"
                >
                  <img src={mailboxIcon} alt="Messages" className="w-full h-full object-contain" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#505050] text-[#d0d0d0] text-xs flex items-center justify-center">
                      {unreadMessages}
                    </span>
                  )}
                </button>

                {/* Logout Button */}
                <button
                  onClick={async () => {
                    if (checkGlobalRateLimit()) return;
                    setIsLoggingOut(true);
                    await supabase.auth.signOut();
                    setCurrentUser(null);
                    navigate(createPageUrl('Auth'));
                  }}
                  className="neuro-base active:neuro-pressed rounded-xl h-11 px-3 smooth-transition hover:scale-105 flex-shrink-0 flex items-center justify-center"
                >
                  <span className="text-[#b0b0b0] font-rotten text-base tracking-wide whitespace-nowrap">Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Global Player Widget - Now visible on all devices */}
        {showPlayer && <GlobalPlayer />}

        {/* Notifications Modal */}
        <NotificationsModal
          open={showNotificationsModal}
          onOpenChange={setShowNotificationsModal}
          notifications={notifications}
          onNotificationClick={handleNotificationClick}
          onMarkAllRead={markAllNotificationsRead}
          unreadCount={unreadNotifications}
        />

        {/* Messages Modal */}
        <MessagesModal
          open={showMessagesModal}
          onOpenChange={setShowMessagesModal}
          messageThreads={messageThreads}
          currentUser={currentUser}
          onSendMessage={async (recipientId, content) => {
            const token = localStorage.getItem('auth_token');
            try {
              await fetch('https://soundope.com/api/messages', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  sender_id: currentUser.id,
                  recipient_id: recipientId,
                  content: content
                })
              });
              loadMessages();
            } catch (error) {
              console.error("Error sending message:", error);
            }
          }}
          onLoadThreadMessages={async (otherUserId) => {
            // Get messages between current user and the other user from Express API
            const token = localStorage.getItem('auth_token');
            try {
              const response = await fetch(`https://soundope.com/api/messages?other_user_id=${otherUserId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (!response.ok) return [];
              const messages = await response.json();
              // Filter to only messages in this conversation
              return messages.filter(msg => 
                (msg.sender_id === currentUser.id && msg.recipient_id === otherUserId) ||
                (msg.sender_id === otherUserId && msg.recipient_id === currentUser.id)
              ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            } catch (error) {
              console.error("Error loading thread messages:", error);
              return [];
            }
          }}
          onMarkThreadRead={async (otherUserId) => {
            const token = localStorage.getItem('auth_token');
            try {
              await fetch(`https://soundope.com/api/messages/read`, {
                method: 'PATCH',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sender_id: otherUserId })
              });
              loadCounts();
            } catch (error) {
              console.error("Error marking thread read:", error);
            }
          }}
        />

        {/* Mobile Search Overlay - REMOVED */}

        {/* Search Results Dropdown */}
        {showSearchDropdown && currentUser && (
          <div 
            className="fixed w-80 rounded-2xl p-4 max-h-[400px] overflow-y-auto shadow-2xl border-2 dropdown-optimized"
            style={{ 
              top: showMobileSearch ? '122px' : '122px',
              right: showMobileSearch ? '16px' : '280px',
              zIndex: 99999,
              backgroundColor: '#1f1f1f',
              borderColor: '#3a3a3a',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.9), 0 0 0 2px #3a3a3a'
            }}
          >
            {isSearching ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#909090]" />
              </div>
            ) : !searchQuery.trim() ? (
              <p className="text-xs text-[#909090] text-center py-8">
                Type to search artists and tracks...
              </p>
            ) : searchResults.users.length === 0 && searchResults.tracks.length === 0 ? (
              <p className="text-xs text-[#909090] text-center py-8">
                No results found for &quot;{searchQuery}&quot;
              </p>
            ) : (
              <div className="space-y-4">
                {searchResults.users.length > 0 && (
                  <div>
                    <p className="text-xs text-[#808080] mb-2 font-medium">Artists</p>
                    <div className="space-y-2">
                      {searchResults.users.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleSearchResultClick('user', user.id)}
                          className="w-full rounded-xl p-3 flex items-center gap-3 smooth-transition hover:scale-[1.01]"
                          style={{ 
                            background: '#2a2a2a',
                            boxShadow: '2px 2px 6px rgba(0, 0, 0, 0.5)'
                          }}
                        >
                          {user.profile_image_url ? (
                            <img
                              src={user.profile_image_url}
                              alt={user.artist_name || user.full_name}
                              className="w-10 h-10 rounded-xl object-cover neuro-pressed"
                              loading="lazy"
                              width="40"
                              height="40"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-xl neuro-pressed flex items-center justify-center">
                              <User className="w-5 h-5 text-[#808080]" />
                            </div>
                          )}
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-medium text-[#e0e0e0] truncate flex items-center gap-1.5">
                              {user.artist_name || user.full_name}
                              {user.has_winner_badge && <WinnerBadge />}
                              <UserLeaderboardBadge userId={user.id} />
                            </p>
                            <p className="text-xs text-[#909090]">
                              {user.review_tier || 'novice'} • {user.points || 0} pts
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults.tracks.length > 0 && (
                  <div>
                    <p className="text-xs text-[#808080] mb-2 font-medium">Tracks</p>
                    <div className="space-y-2">
                      {searchResults.tracks.map((track) => (
                        <button
                          key={track.id}
                          onClick={() => handleSearchResultClick('track', track.id)}
                          className="w-full rounded-xl p-3 flex items-center gap-3 smooth-transition hover:scale-[1.01]"
                          style={{ 
                            background: '#2a2a2a',
                            boxShadow: '2px 2px 6px rgba(0, 0, 0, 0.5)'
                          }}
                        >
                          <img
                            src={track.cover_image_url}
                            alt={track.title}
                            className={`w-10 h-10 rounded-xl object-cover neuro-pressed ${voteBoostMode ? 'cursor-pointer ring-2 ring-muted-foreground' : ''}`}
                            onClick={(e) => {
                              if (voteBoostMode && isShiftPressed) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleTrackClickForBoost(e, track.id);
                              }
                            }}
                            loading="lazy"
                            width="40"
                            height="40"
                          />
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-medium text-[#e0e0e0] truncate">
                              {track.title}
                            </p>
                            <p className="text-xs text-[#909090] truncate flex items-center gap-1.5">
                              {track.artist_name}
                              {track.artist_has_badge && <WinnerBadge />}
                              {track.artist_id && <ArtistLeaderboardBadge artistId={track.artist_id} />}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* Contest Banner - Fixed below header */}
        {(location.pathname === '/' || location.pathname === createPageUrl("Home")) && <ContestBanner />}

        {/* Secondary Navigation Bar - Visible to Everyone on Home */}
        {(location.pathname === '/' || location.pathname === createPageUrl("Home")) && (
          <div 
            className="fixed left-0 right-0 h-14 border-b border-[#2a2a2a] overflow-x-auto"
            style={{ 
              zIndex: 50, 
              backgroundColor: '#141414',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
              top: 'calc(3.5rem + 40px + env(safe-area-inset-top, 0px))' // Header + Contest Banner height
            }}
          >
            <div className="max-w-screen-xl mx-auto h-full px-2 md:px-4 flex items-center justify-center gap-2 md:gap-6 lg:gap-8 flex-nowrap min-w-max">
              <Link
                to={createPageUrl("HowItWorks")}
                className={`text-xs md:text-sm font-medium smooth-transition py-2 px-2 md:px-4 rounded-xl whitespace-nowrap ${
                  location.pathname === createPageUrl("HowItWorks")
                    ? 'text-[#e0e0e0] neuro-pressed'
                    : 'text-[#a0a0a0] hover:text-[#d0d0d0] neuro-flat'
                }`}
              >
                How It Works
              </Link>
              <Link
                to={createPageUrl("ForArtists")}
                className={`text-xs md:text-sm font-medium smooth-transition py-2 px-2 md:px-4 rounded-xl whitespace-nowrap ${
                  location.pathname === createPageUrl("ForArtists")
                    ? 'text-[#e0e0e0] neuro-pressed'
                    : 'text-[#a0a0a0] hover:text-[#d0d0d0] neuro-flat'
                }`}
              >
                For Artists
              </Link>
              <Link
                to={createPageUrl("ForCurators")}
                className={`text-xs md:text-sm font-medium smooth-transition py-2 px-2 md:px-4 rounded-xl whitespace-nowrap ${
                  location.pathname === createPageUrl("ForCurators")
                    ? 'text-[#e0e0e0] neuro-pressed'
                    : 'text-[#a0a0a0] hover:text-[#d0d0d0] neuro-flat'
                }`}
              >
                Earn Money Reviewing
              </Link>
              <Link
                to={createPageUrl("FAQ")}
                className={`text-xs md:text-sm font-medium smooth-transition py-2 px-2 md:px-4 rounded-xl whitespace-nowrap ${
                  location.pathname === createPageUrl("FAQ")
                    ? 'text-[#e0e0e0] neuro-pressed'
                    : 'text-[#a0a0a0] hover:text-[#d0d0d0] neuro-flat'
                }`}
              >
                FAQ
              </Link>
              <Link
                to="/blog"
                className={`text-xs md:text-sm font-medium smooth-transition py-2 px-2 md:px-4 rounded-xl whitespace-nowrap ${
                  location.pathname === '/blog'
                    ? 'text-[#e0e0e0] neuro-pressed'
                    : 'text-[#a0a0a0] hover:text-[#d0d0d0] neuro-flat'
                }`}
              >
                Blog
              </Link>
            </div>
          </div>
        )}

        {/* Dev Unlock Modal */}
        {showDevModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="neuro-base rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {!devUnlocked ? (
                <>
                  <h2 className="text-xl font-light text-[#d0d0d0] mb-4 text-center">
                    Dev Unlock
                  </h2>
                  <p className="text-sm text-[#a0a0a0] mb-4 text-center">
                    Dev tools are for testing only. Click Unlock to add test
                    credits and votes to your account.
                  </p>
                  {devError && (
                    <p className="text-xs text-[#b09090] text-center mb-4">
                      {devError}
                    </p>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDevModal(false);
                        setDevPassword("");
                        setDevError("");
                        setDevUnlocked(false);
                      }}
                      className="flex-1 neuro-flat rounded-2xl py-3 text-sm text-[#808080]"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDevUnlock}
                      className="flex-1 neuro-base active:neuro-pressed rounded-2xl py-3 text-sm text-[#a0a0a0]"
                    >
                      Unlock
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-light text-[#d0d0d0]">Admin Panel</h2>
                    <button
                      onClick={() => {
                        setShowDevModal(false);
                        setDevPassword("");
                        setDevError("");
                        // Don't disable vote boost mode when closing - let it persist
                      }}
                      className="neuro-flat rounded-xl p-2 text-[#808080] hover:text-[#a0a0a0]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <AdminPanel />
                  
                  <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
                    <BlogAdmin />
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#2a2a2a] space-y-3">
                    <h3 className="text-sm font-medium text-[#a0a0a0] mb-2">Dev Tools</h3>
                    <button
                      onClick={handleRestoreLeaderboard}
                      className="w-full neuro-base active:neuro-pressed rounded-2xl py-3 text-sm text-[#a0a0a0]"
                    >
                      Restore All Tracks to Leaderboard
                    </button>
                    <button
                      onClick={handleEnableVoteBoost}
                      className={`w-full rounded-2xl py-3 text-sm ${
                        voteBoostMode 
                          ? 'neuro-pressed text-[#808080]' 
                          : 'neuro-base active:neuro-pressed text-[#a0a0a0]'
                      }`}
                    >
                      {voteBoostMode ? 'Vote Boost Mode: ON (Click track images)' : 'Enable Vote Boost Mode'}
                    </button>
                    {voteBoostMode && (
                      <button
                        onClick={() => {
                          setVoteBoostMode(false);
                          setVoteInputVisible(false);
                          // Only clear localStorage in development
                          if (import.meta.env.DEV) {
                            localStorage.removeItem('vote_boost_mode');
                            localStorage.removeItem('dev_unlocked');
                          }
                          toast.info("Vote boost mode disabled");
                        }}
                        className="w-full neuro-flat rounded-2xl py-2 text-xs text-[#808080]"
                      >
                        Disable Vote Boost Mode
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Vote Boost Input Form */}
        {voteInputVisible && (
          <div
            className="fixed z-[10001] bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl p-4 shadow-lg"
            style={{
              left: `${voteInputPosition.x + 20}px`,
              top: `${voteInputPosition.y}px`,
              transform: 'translateY(-50%)'
            }}
          >
            <p className="text-xs text-[#a0a0a0] mb-2">Add votes:</p>
            <input
              ref={voteInputRef}
              type="number"
              value={voteInputValue}
              onChange={(e) => setVoteInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmitVoteBoost()}
              placeholder="Enter number"
              className="w-32 bg-[#0a0a0a] border border-[#2a2a2a] text-[#d0d0d0] rounded-lg p-2 mb-2 text-sm"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSubmitVoteBoost}
                className="flex-1 neuro-base active:neuro-pressed rounded-lg py-1.5 text-xs text-[#a0a0a0]"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setVoteInputVisible(false);
                  setVoteInputValue("");
                  setSelectedTrackForBoost(null);
                }}
                className="flex-1 neuro-flat rounded-lg py-1.5 text-xs text-[#808080]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}


        <main
          className={`pb-8 md:pb-36 min-h-screen ${
            currentUser
              ? location.pathname === createPageUrl("Home") 
                ? 'pt-[160px] md:pt-[224px]' // Header (64px/128px) + Contest Banner (40px) + Secondary Nav (56px)
                : 'pt-20 md:pt-36'
              : location.pathname === createPageUrl("Home")
                ? 'pt-[160px] md:pt-[192px]' // Header (64px/96px) + Contest Banner (40px) + Secondary Nav (56px)
                : 'pt-20 md:pt-28'
          }`}
        >
          {children}
        </main>

        <Footer 
          onDeadEntLogoClick={handleDeadEntLogoClick}
          onSoundopeLogoClick={handleSoundopeLogoClick}
        />
        

        {/* Mobile Sidebar */}
        <MobileSidebar />

        {/* AI Support Chat Bot */}
        <SupportChatBot />

        {/* Bottom Navigation - Desktop Only */}
        <div 
          className={`hidden md:block fixed bottom-0 left-0 right-0 z-[10000] transition-all duration-300 ease-in-out ${
            desktopNavCollapsed ? 'translate-y-[calc(100%-40px)]' : 'translate-y-0'
          }`}
        >
          {/* Toggle Button */}
          <div className="flex justify-center">
            <button
              onClick={() => setDesktopNavCollapsed(!desktopNavCollapsed)}
              className="bg-[#1a1a1a] border border-b-0 border-[#2a2a2a] rounded-t-xl px-6 py-2 flex items-center justify-center hover:bg-[#222] transition-colors"
              style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.5)' }}
              aria-label={desktopNavCollapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {desktopNavCollapsed ? (
                <ChevronLeft className="w-4 h-4 text-[#808080] rotate-90" />
              ) : (
                <ChevronRight className="w-4 h-4 text-[#808080] rotate-90" />
              )}
            </button>
          </div>
          
          {/* Nav Content */}
          <nav 
            className="neuro-base border-t border-[#1f1f1f] overflow-x-auto overflow-y-hidden"
            style={{ 
              backgroundColor: '#141414',
            }}
          >
            <div className="flex items-center justify-center gap-3 p-4 min-w-max mx-auto">
              {navItems.map(({ icon, label, tooltip, path, glowColor }) => (
                <TooltipProvider key={path} delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={path}
                        onClick={(e) => {
                          if (checkNavRateLimit(e)) {
                            return;
                          }
                          hapticLight();
                        }}
                        className={`flex flex-col items-center justify-center gap-1.5 smooth-transition bg-black ${
                          isActive(path) ? 'ring-2 ring-[#2a2a2a]' : ''
                        } px-5 py-3 rounded-xl hover:scale-105 transition-all duration-200`}
                        style={{
                          '--glow-color': glowColor,
                          border: '1px solid transparent',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 15px rgba(${glowColor}, 0.8)`;
                          e.currentTarget.style.borderColor = `rgba(${glowColor}, 0.6)`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                        aria-label={`Navigate to ${label} page`}
                      >
                        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                          <img
                            src={icon}
                            alt={label}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            decoding="async"
                            width="48"
                            height="48"
                            fetchpriority="low"
                          />
                        </div>
                        <span 
                          className="text-sm font-medium font-simple-minimalist text-[#d0d0d0] leading-tight text-center tracking-wider whitespace-nowrap"
                        >
                          {label}
                        </span>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </PlayerProvider>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  currentPageName: PropTypes.string,
};

export default Layout;
